import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { confirmedPosition, searchAddress } from "../Services/addressGeocoding";

import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";

import {
  LuArrowLeft,
  LuArrowRight,
  LuChevronDown,
  LuLocateFixed,
  LuMapPin,
  LuSearch,
} from "react-icons/lu";

import "leaflet/dist/leaflet.css";

import citiesAndDelegations from "../data/citiesAndDelegations.js";
import postalCodes from "../data/postalCodes.js";
import citiesCoordinates from "../data/delegationsCoordinates.js";

const DEFAULT_POSITION = [33.8869, 9.5375];

function MapUpdater({ position, zoom }) {
  const map = useMap();

  useEffect(() => {
    if (
      Array.isArray(position) &&
      position.length === 2
    ) {
      map.setView(position, zoom, {
        animate: true,
      });
    }
  }, [map, position, zoom]);

  return null;
}

const CreateSectionAdress = ({
  formData,
  setFormData,
  handlePrevious,
  handleContinue,
  hideNavigation = false,
}) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState(
    formData.Adresse || ""
  );

  const initialPosition = confirmedPosition(formData.Latitude, formData.Longitude);
  const [position, setPosition] = useState(initialPosition || DEFAULT_POSITION);
  const [zoom, setZoom] = useState(initialPosition ? 13 : 7);
  const [approximate, setApproximate] = useState(false);
  const requestVersion = useRef(0);
  const requestController = useRef(null);
  const requestKind = useRef(null);
  const hasPosition = Boolean(confirmedPosition(formData.Latitude, formData.Longitude));
  useEffect(() => () => { requestVersion.current++; requestController.current?.abort(); }, []);

  const [searching, setSearching] =
    useState(false);

  const [searchError, setSearchError] =
    useState("");

  const mapRef = useRef(null);

  useEffect(() => {
    setSearchTerm(formData.Adresse || "");
  }, [formData.Adresse]);

  useEffect(() => {
    const existing = confirmedPosition(formData.Latitude, formData.Longitude);
    if (existing) { setPosition(existing); return; }
    const selectedCity = formData.Ville;

    if (
      selectedCity &&
      citiesCoordinates[selectedCity]
    ) {
      const { lat, lng } =
        citiesCoordinates[selectedCity];

      const nextPosition = [
        Number(lat),
        Number(lng),
      ];

      setPosition(nextPosition);
      setZoom(12);

    }
  }, [formData.Ville, formData.Latitude, formData.Longitude]);

  const updateField = (field, value) => {
    setFormData((previousValue) => ({
      ...previousValue,
      [field]: value,
    }));
  };

  const invalidatePosition = () => {
    requestController.current?.abort();
    requestVersion.current++; setSearching(false); setSearchError(""); setApproximate(false);
    setFormData(previous => ({ ...previous, Latitude: null, Longitude: null }));
  };

  const onChange = (event) => {
    const { id, value } = event.target;
    if (id === "Delegation") invalidatePosition();

    if (id === "Adresse") {
      // Typing an address must not cancel the current delegation lookup.
      if (requestKind.current === 'address') {
        requestController.current?.abort();
        requestVersion.current++;
        setSearching(false);
        setSearchError('');
        // Keep the last applied location while the new address is still a draft.
      }
      setSearchTerm(value);
    }

    if (id === "Delegation") {
      setFormData((previousValue) => ({
        ...previousValue,
        Delegation: value,
        CodePostal:
          previousValue.CodePostal ||
          postalCodes[value] ||
          "",
      }));

      if (value && formData.Ville) locate({ ...formData, Delegation: value, Adresse: '' });

      return;
    }

    updateField(id, value);
  };

  const handleCityChange = (event) => {
    const city = event.target.value;
    invalidatePosition();

    setFormData((previousValue) => ({
      ...previousValue,
      Ville: city,
      Delegation: "",
    }));
  };

  const locate = async (fields) => {
    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    requestKind.current = fields.Adresse?.trim() ? 'address' : 'delegation';
    const version = ++requestVersion.current;
    setSearching(true); setSearchError(''); setApproximate(false);
    setFormData(previous => ({ ...previous, Latitude: null, Longitude: null }));
    try {
      const result = await searchAddress(fields, { signal: controller.signal });
      if (version !== requestVersion.current) return;
      if (!result) { setSearchError(t('create.address_not_found')); return; }
      setPosition(result.position); setZoom(result.approximate ? 12 : 15);
      setApproximate(result.approximate);
      setFormData(previous => ({ ...previous, Latitude: result.position[0], Longitude: result.position[1] }));
    } catch {
      if (version === requestVersion.current) setSearchError(t('create.address_search_error'));
    } finally {
      if (version === requestVersion.current) setSearching(false);
    }
  };

  const handleSearch = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (searching) return;
    if (!formData.Ville || !formData.Delegation) {
      setSearchError(t('create.address_fields_required')); return;
    }
    locate({ ...formData, Adresse: searchTerm });
  };

  const canContinue =
    Boolean(formData.Ville) &&
    Boolean(formData.Delegation);

  const continueToNextStep = () => {
    if (!canContinue) {
      setSearchError(
        t(
          "create.address_fields_required",
          "Veuillez renseigner la ville et la délégation."
        )
      );
      return;
    }

    setSearchError("");
    handleContinue();
  };

  return (
    <section>
      <div className="lina-location-fields grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Adresse */}
        <div>
          <label
            htmlFor="Adresse"
            className="mb-2 block text-sm font-black text-slate-700"
          >
            {t("address", "Adresse")}

          </label>

          <div
            className="flex gap-2"
          >
            <div className="relative min-w-0 flex-1">
              <LuMapPin
                size={18}
                className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="text"
                id="Adresse"
                value={searchTerm}
                onChange={onChange}
                onKeyDown={event => { if (event.key === "Enter") handleSearch(event); }}
                placeholder={t(
                  "enterAdres",
                  "Saisissez une adresse"
                )}
                className="
                  h-12
                  w-full
                  rounded-2xl
                  border
                  border-slate-200
                  bg-white
                  ps-11
                  pe-4
                  text-sm
                  font-medium
                  text-slate-900
                  outline-none
                  transition
                  placeholder:text-slate-400
                  hover:border-slate-300
                  focus:border-green-500
                  focus:ring-4
                  focus:ring-green-500/10
                "
              />
            </div>

            <button
              type="button"
              onClick={handleSearch}
              disabled={searching}
              aria-label={t(
                "create.search_address",
                "Rechercher l’adresse"
              )}
              title={t(
                "create.search_address",
                "Rechercher l’adresse"
              )}
              className="
                flex
                h-12
                w-12
                shrink-0
                items-center
                justify-center
                rounded-2xl
                bg-green-600
                text-white
                shadow-lg
                shadow-green-600/20
                transition
                hover:bg-green-700
                disabled:cursor-not-allowed
                disabled:bg-slate-300
                focus:outline-none
                focus:ring-4
                focus:ring-green-500/20
              "
            >
              {searching ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              ) : (
                <LuSearch size={18} />
              )}
            </button>
          </div>
        </div>

        {/* Ville */}
        <div>
          <label
            htmlFor="Ville"
            className="mb-2 block text-sm font-black text-slate-700"
          >
            {t("city", "Ville")}
            <span className="ms-1 text-red-500">
              *
            </span>
          </label>

          <div className="relative">
            <select
              required
              id="Ville"
              value={formData.Ville}
              onChange={handleCityChange}
              className="
                h-12
                w-full
                appearance-none
                rounded-2xl
                border
                border-slate-200
                bg-white
                px-4
                pe-10
                text-sm
                font-medium
                text-slate-900
                outline-none
                transition
                hover:border-slate-300
                focus:border-green-500
                focus:ring-4
                focus:ring-green-500/10
              "
            >
              <option value="">
                {t(
                  "SelectCity",
                  "Sélectionner une ville"
                )}
              </option>

              {Object.keys(
                citiesAndDelegations
              ).map((city) => (
                <option key={city} value={city}>
                  {t(`cities.${city}`, city)}
                </option>
              ))}
            </select>

            <LuChevronDown
              size={16}
              className="pointer-events-none absolute end-4 top-1/2 -translate-y-1/2 text-slate-400"
            />
          </div>
        </div>

        {/* Code postal */}
        <div>
          <label
            htmlFor="CodePostal"
            className="mb-2 block text-sm font-black text-slate-700"
          >
            {t(
              "postal_code",
              "Code postal"
            )}
          </label>

          <input
            type="text"
            inputMode="numeric"
            id="CodePostal"
            maxLength={4}
            value={formData.CodePostal || ""}
            onChange={(event) => {
              const normalizedValue =
                event.target.value
                  .replace(/\D/g, "")
                  .slice(0, 4);

              updateField(
                "CodePostal",
                normalizedValue
              );
            }}
            placeholder="3000"
            className="
              h-12
              w-full
              rounded-2xl
              border
              border-slate-200
              bg-white
              px-4
              text-sm
              font-medium
              text-slate-900
              outline-none
              transition
              placeholder:text-slate-400
              hover:border-slate-300
              focus:border-green-500
              focus:ring-4
              focus:ring-green-500/10
            "
          />
        </div>

        {/* Délégation */}
        <div>
          <label
            htmlFor="Delegation"
            className="mb-2 block text-sm font-black text-slate-700"
          >
            {t(
              "delegation",
              "Délégation"
            )}
            <span className="ms-1 text-red-500">
              *
            </span>
          </label>

          <div className="relative">
            <select
              required
              id="Delegation"
              value={formData.Delegation}
              onChange={onChange}
              disabled={!formData.Ville}
              className="
                h-12
                w-full
                appearance-none
                rounded-2xl
                border
                border-slate-200
                bg-white
                px-4
                pe-10
                text-sm
                font-medium
                text-slate-900
                outline-none
                transition
                hover:border-slate-300
                focus:border-green-500
                focus:ring-4
                focus:ring-green-500/10
                disabled:cursor-not-allowed
                disabled:bg-slate-100
                disabled:text-slate-400
              "
            >
              <option value="">
                {t(
                  "SelectDelegation",
                  "Sélectionner une délégation"
                )}
              </option>

              {citiesAndDelegations[
                formData.Ville
              ]?.map((delegation) => (
                <option
                  key={delegation}
                  value={delegation}
                >
                  {t(
                    `delegations.${formData.Ville}.${delegation}`,
                    delegation
                  )}
                </option>
              ))}
            </select>

            <LuChevronDown
              size={16}
              className="pointer-events-none absolute end-4 top-1/2 -translate-y-1/2 text-slate-400"
            />
          </div>
        </div>
      </div>

      {/* Erreur */}
      {searchError && (
        <div
          role="alert"
          className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-700"
        >
          {searchError}
        </div>
      )}

      {approximate && hasPosition && <p role="status" className="mt-3 text-sm text-slate-600">{t("create.area_position")}</p>}

      {/* Carte */}
      <div className="mt-6 overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2">
            <LuLocateFixed
              size={18}
              className="text-green-600"
            />

            <span className="text-sm font-black text-slate-800">
              {t(
                "create.map_preview",
                "Aperçu sur la carte"
              )}
            </span>
          </div>

          <span className="text-xs text-slate-400">
            {hasPosition ? `${Number(position[0]).toFixed(4)}, ${Number(position[1]).toFixed(4)}` : t("create.position_unconfirmed")}
          </span>
        </div>

        <MapContainer
          center={position}
          zoom={zoom}
          ref={mapRef}
          scrollWheelZoom={false}
          className="h-[300px] w-full sm:h-[360px]"
        >
          <MapUpdater
            position={position}
            zoom={zoom}
          />

          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          {hasPosition && <Marker position={position}>
            <Popup>
              <strong>
                {formData.Adresse ||
                  t(
                    "address",
                    "Adresse"
                  )}
              </strong>

              <br />

              {[
                formData.Delegation,
                formData.Ville,
              ]
                .filter(Boolean)
                .join(", ")}
            </Popup>
          </Marker>}
        </MapContainer>
      </div>

      {/* Navigation */}
      {!hideNavigation && <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:justify-between">
        <button
          type="button"
          onClick={handlePrevious}
          className="
            inline-flex
            h-12
            items-center
            justify-center
            gap-2
            rounded-2xl
            border
            border-slate-200
            bg-white
            px-6
            text-sm
            font-bold
            text-slate-700
            transition
            hover:border-slate-300
            hover:bg-slate-100
            focus:outline-none
            focus:ring-4
            focus:ring-slate-500/10
          "
        >
          <LuArrowLeft
            size={17}
            className="rtl:rotate-180"
          />

          {t("previous", "Précédent")}
        </button>

        <button
          type="button"
          onClick={continueToNextStep}
          className="
            inline-flex
            h-12
            items-center
            justify-center
            gap-2
            rounded-2xl
            bg-slate-950
            px-7
            text-sm
            font-black
            text-white
            shadow-lg
            transition-all
            hover:-translate-y-0.5
            hover:bg-green-600
            focus:outline-none
            focus:ring-4
            focus:ring-green-500/20
          "
        >
          {t("Continuer", "Continuer")}

          <LuArrowRight
            size={17}
            className="rtl:rotate-180"
          />
        </button>
      </div>}
    </section>
  );
};

export default CreateSectionAdress;
